import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import GlobalHeader from "../../components/ui/GlobalHeader";
import SidebarNavigation from "../../components/ui/SidebarNavigation";
import BreadcrumbTrail from "../../components/ui/BreadcrumbTrail";
import QuickActionToolbar from "../../components/ui/QuickActionToolbar";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";

// Import components
import CampaignFilters from "./components/CampaignFilters";
import CampaignTable from "./components/CampaignTable";
import CampaignModal from "./components/CampaignModal";
import BulkActionsBar from "./components/BulkActionsBar";
import CampaignStats from "./components/CampaignStats";
import NavigationHeader from "components/ui/NavigationHeader";
import MainNavigation from "components/ui/MainNavigation";
import { useTranslation } from "react-i18next";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteAdvertismentService,
  getAdvertisementsService,
} from "redux/services/general_service";
import { toast } from "react-toastify";

const SecondAdvertisementManagement = () => {
  const { t } = useTranslation();

  const { advertisment } = useSelector((state) => state.general);

  const dispatch = useDispatch();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Mock campaign data
  const mockCampaigns = [
    {
      id: 1,
      name: "Summer Sale 2024",
      description: "Promote summer collection with 30% discount",
      type: "display",
      status: "active",
      budget: 5000,
      spent: 3200,
      dailyBudget: 200,
      startDate: "2024-06-01",
      endDate: "2024-08-31",
      impressions: 125000,
      clicks: 3750,
      ctr: 3.0,
      roi: 245,
      mediaUrl:
        "https://images.pexels.com/photos/1488463/pexels-photo-1488463.jpeg",
      targetAudience: ["young_adults", "professionals"],
      locations: ["us", "ca"],
      objectives: "sales",
      bidStrategy: "auto",
      keywords: "summer, sale, fashion, discount",
    },
    {
      id: 2,
      name: "Back to School Campaign",
      description: "Target students and parents for school supplies",
      type: "social",
      status: "paused",
      budget: 3000,
      spent: 1800,
      dailyBudget: 150,
      startDate: "2024-07-15",
      endDate: "2024-09-15",
      impressions: 89000,
      clicks: 2670,
      ctr: 3.2,
      roi: 180,
      mediaUrl:
        "https://images.pexels.com/photos/159844/cellular-education-classroom-159844.jpeg",
      targetAudience: ["students", "families"],
      locations: ["us"],
      objectives: "traffic",
      bidStrategy: "manual",
      keywords: "school, supplies, education, students",
    },
    {
      id: 3,
      name: "Holiday Gift Guide",
      description: "Showcase holiday gift collection",
      type: "video",
      status: "scheduled",
      budget: 8000,
      spent: 0,
      dailyBudget: 300,
      startDate: "2024-11-01",
      endDate: "2024-12-25",
      impressions: 0,
      clicks: 0,
      ctr: 0,
      roi: 0,
      mediaUrl:
        "https://images.pexels.com/photos/1303081/pexels-photo-1303081.jpeg",
      targetAudience: ["families", "professionals"],
      locations: ["us", "ca", "uk"],
      objectives: "awareness",
      bidStrategy: "target_roas",
      keywords: "holiday, gifts, christmas, presents",
    },
    {
      id: 4,
      name: "New Product Launch",
      description: "Introduce latest product line to market",
      type: "search",
      status: "active",
      budget: 4500,
      spent: 2100,
      dailyBudget: 180,
      startDate: "2024-08-01",
      endDate: "2024-10-31",
      impressions: 95000,
      clicks: 4750,
      ctr: 5.0,
      roi: 320,
      mediaUrl:
        "https://images.pexels.com/photos/267350/pexels-photo-267350.jpeg",
      targetAudience: ["professionals", "entrepreneurs"],
      locations: ["us", "uk", "au"],
      objectives: "conversions",
      bidStrategy: "target_cpa",
      keywords: "new product, innovation, technology",
    },
    {
      id: 5,
      name: "Brand Awareness Drive",
      description: "Increase brand visibility and recognition",
      type: "display",
      status: "completed",
      budget: 2500,
      spent: 2500,
      dailyBudget: 100,
      startDate: "2024-05-01",
      endDate: "2024-05-31",
      impressions: 180000,
      clicks: 5400,
      ctr: 3.0,
      roi: 150,
      mediaUrl:
        "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg",
      targetAudience: ["young_adults", "professionals", "families"],
      locations: ["us", "ca", "uk", "au"],
      objectives: "awareness",
      bidStrategy: "auto",
      keywords: "brand, awareness, recognition",
    },
    {
      id: 6,
      name: "Email Newsletter Promo",
      description: "Drive email subscriptions with exclusive offers",
      type: "email",
      status: "draft",
      budget: 1500,
      spent: 0,
      dailyBudget: 75,
      startDate: "2024-09-01",
      endDate: "2024-09-30",
      impressions: 0,
      clicks: 0,
      ctr: 0,
      roi: 0,
      mediaUrl:
        "https://images.pexels.com/photos/1591062/pexels-photo-1591062.jpeg",
      targetAudience: ["professionals", "entrepreneurs"],
      locations: ["us"],
      objectives: "leads",
      bidStrategy: "manual",
      keywords: "newsletter, email, subscription, exclusive",
    },
  ];

  useEffect(() => {
    dispatch(getAdvertisementsService({ page: 1, type: "banner" }));
    setCampaigns(mockCampaigns);
    setFilteredCampaigns(mockCampaigns);
  }, []);
  const user = {
    name: t("userName"),
    email: t("userEmail"),
    avatar:
      "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150",
  };
  // Filter campaigns based on search and filters
  const applyFilters = (filters) => {
    let filtered = campaigns;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered?.filter(
        (campaign) =>
          campaign?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          campaign?.description
            ?.toLowerCase()
            ?.includes(searchTerm?.toLowerCase()),
      );
    }

    // Apply status filter
    if (filters?.status) {
      filtered = filtered?.filter(
        (campaign) => campaign?.status === filters?.status,
      );
    }

    // Apply campaign type filter
    if (filters?.campaignType) {
      filtered = filtered?.filter(
        (campaign) => campaign?.type === filters?.campaignType,
      );
    }

    // Apply budget range filter
    if (filters?.budgetRange?.min) {
      filtered = filtered?.filter(
        (campaign) => campaign?.budget >= parseFloat(filters?.budgetRange?.min),
      );
    }
    if (filters?.budgetRange?.max) {
      filtered = filtered?.filter(
        (campaign) => campaign?.budget <= parseFloat(filters?.budgetRange?.max),
      );
    }

    // Apply date range filter
    if (filters?.dateRange?.start) {
      filtered = filtered?.filter(
        (campaign) =>
          new Date(campaign.startDate) >= new Date(filters.dateRange.start),
      );
    }
    if (filters?.dateRange?.end) {
      filtered = filtered?.filter(
        (campaign) =>
          new Date(campaign.endDate) <= new Date(filters.dateRange.end),
      );
    }

    // Apply target audience filter
    if (filters?.targetAudience?.length > 0) {
      filtered = filtered?.filter((campaign) =>
        filters?.targetAudience?.some((audience) =>
          campaign?.targetAudience?.includes(audience),
        ),
      );
    }

    // Apply search term filter from filters
    if (filters?.searchTerm) {
      filtered = filtered?.filter(
        (campaign) =>
          campaign?.name
            ?.toLowerCase()
            ?.includes(filters?.searchTerm?.toLowerCase()) ||
          campaign?.description
            ?.toLowerCase()
            ?.includes(filters?.searchTerm?.toLowerCase()),
      );
    }

    setFilteredCampaigns(filtered);
  };

  // Sort campaigns
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig?.key === key && sortConfig?.direction === "asc") {
      direction = "desc";
    }

    const sortedCampaigns = [...filteredCampaigns]?.sort((a, b) => {
      if (a?.[key] < b?.[key]) return direction === "asc" ? -1 : 1;
      if (a?.[key] > b?.[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredCampaigns(sortedCampaigns);
    setSortConfig({ key, direction });
  };

  // Handle campaign actions
  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setShowCampaignModal(true);
  };

  const handlePauseResumeCampaign = (campaign) => {
    const newStatus = campaign?.status === "active" ? "paused" : "active";
    const updatedCampaigns = campaigns?.map((c) =>
      c?.id === campaign?.id ? { ...c, status: newStatus } : c,
    );
    setCampaigns(updatedCampaigns);
    applyFilters({});
  };
  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  const handleDuplicateCampaign = (campaign) => {
    const duplicatedCampaign = {
      ...campaign,
      id: Date.now(),
      name: `${campaign?.name} (Copy)`,
      status: "draft",
      spent: 0,
      impressions: 0,
      clicks: 0,
      ctr: 0,
      roi: 0,
    };
    const updatedCampaigns = [...campaigns, duplicatedCampaign];
    setCampaigns(updatedCampaigns);
    applyFilters({});
  };

  const handleDeleteCampaign = (campaign) => {
    if (window.confirm(`Are you sure you want to delete "${campaign?.id}"?`)) {
      dispatch(deleteAdvertismentService(campaign?.id)).then((response) => {
        if (
          response?.payload?.status == 200 ||
          response?.payload?.status == 204
        ) {
          toast("تم الحذف بنجاح !");
          dispatch(getAdvertisementsService({ page: 1, type: "banner" }));
        }
      });
    }
  };

  const handleSaveCampaign = (campaignData) => {
    if (editingCampaign) {
      // Update existing campaign
      const updatedCampaigns = campaigns?.map((c) =>
        c?.id === editingCampaign?.id ? { ...c, ...campaignData } : c,
      );
      setCampaigns(updatedCampaigns);
    } else {
      // Create new campaign
      const newCampaign = {
        ...campaignData,
        id: Date.now(),
        spent: 0,
        impressions: 0,
        clicks: 0,
        ctr: 0,
        roi: 0,
        status: "draft",
        mediaUrl:
          campaignData?.mediaFiles?.[0]?.url ||
          "https://images.pexels.com/photos/267350/pexels-photo-267350.jpeg",
      };
      setCampaigns([...campaigns, newCampaign]);
    }
    setEditingCampaign(null);
    applyFilters({});
  };

  const handleBulkAction = (action) => {
    const selectedCampaignObjects = campaigns?.filter((c) =>
      selectedCampaigns?.includes(c?.id),
    );

    switch (action) {
      case "activate":
        const activatedCampaigns = campaigns?.map((c) =>
          selectedCampaigns?.includes(c?.id) ? { ...c, status: "active" } : c,
        );
        setCampaigns(activatedCampaigns);
        break;
      case "pause":
        const pausedCampaigns = campaigns?.map((c) =>
          selectedCampaigns?.includes(c?.id) ? { ...c, status: "paused" } : c,
        );
        setCampaigns(pausedCampaigns);
        break;
      case "duplicate":
        const duplicates = selectedCampaignObjects?.map((campaign) => ({
          ...campaign,
          id: Date.now() + Math.random(),
          name: `${campaign?.name} (Copy)`,
          status: "draft",
          spent: 0,
          impressions: 0,
          clicks: 0,
          ctr: 0,
          roi: 0,
        }));
        setCampaigns([...campaigns, ...duplicates]);
        break;
      case "export":
        console.log("Exporting campaigns:", selectedCampaignObjects);
        break;
      case "delete":
        if (
          window.confirm(
            `Are you sure you want to delete ${selectedCampaigns?.length} campaigns?`,
          )
        ) {
          const remainingCampaigns = campaigns?.filter(
            (c) => !selectedCampaigns?.includes(c?.id),
          );
          setCampaigns(remainingCampaigns);
        }
        break;
    }

    setSelectedCampaigns([]);
    applyFilters({});
  };

  // Apply search filter when search term changes
  useEffect(() => {
    applyFilters({});
  }, [searchTerm, campaigns]);

  return (
    <>
      <Helmet>
        <title>Advertisement Management - Business Manager Pro</title>
        <meta
          name="description"
          content="Manage and optimize your advertising campaigns with comprehensive analytics and performance tracking."
        />
      </Helmet>

      {/* Navigation Header
      <NavigationHeader
        user={user}
        onMenuToggle={handleSidebarToggle}
        sidebarCollapsed={sidebarCollapsed}
      />

     
      <MainNavigation
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      /> */}

      <div className="p-6">
        {/* Breadcrumb Navigation
            <BreadcrumbNavigation /> */}

        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t("advertisement_management")}
            </h1>
          </div>

          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <Button
              onClick={() => {
                setEditingCampaign(null);
                setShowCampaignModal(true);
              }}
              iconName="Plus"
              iconPosition="left"
            >
              {t("create_campaign")}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <CampaignTable
              campaigns={advertisment}
              selectedCampaigns={selectedCampaigns}
              onSelectionChange={setSelectedCampaigns}
              onEdit={handleEditCampaign}
              onPauseResume={handlePauseResumeCampaign}
              onDuplicate={handleDuplicateCampaign}
              onDelete={handleDeleteCampaign}
              sortConfig={sortConfig}
              onSort={handleSort}
            />

            {filteredCampaigns?.length === 0 && (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon
                  name="Megaphone"
                  size={48}
                  className="text-muted-foreground mx-auto mb-4"
                />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  No campaigns found
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchTerm || campaigns?.length === 0
                    ? "Try adjusting your search or filters to find campaigns."
                    : "Get started by creating your first advertising campaign."}
                </p>
                <Button
                  onClick={() => {
                    setEditingCampaign(null);
                    setShowCampaignModal(true);
                  }}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Create Campaign
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Campaign Modal */}
      <CampaignModal
        isOpen={showCampaignModal}
        onClose={() => {
          setShowCampaignModal(false);
          setEditingCampaign(null);
        }}
        campaign={editingCampaign}
        onSave={handleSaveCampaign}
      />
    </>
  );
};

export default SecondAdvertisementManagement;
